import React from 'react';
import { T, useTranslate } from '@tolgee/react';
import { TrackingTruck } from '../orderTracking.interface';
import { isTruckAssigned } from '../orderTracking.utils';
import { cn } from '../../../../lib/utils';

interface TruckSwitcherProps {
	trucks: TrackingTruck[];
	selectedTruckId?: number;
	onSelect: (truckId?: number) => void;
	className?: string;
}

const TruckSwitcher: React.FC<TruckSwitcherProps> = ({
	trucks,
	selectedTruckId,
	onSelect,
	className,
}) => {
	const { t } = useTranslate();

	if (trucks.length < 2) return null;

	return (
		<div
			className={cn(
				'flex flex-col min-w-[220px] max-w-[calc(100vw-32px)] bg-white border border-solid border-track-line rounded-[10px] shadow-[0_2px_12px_rgba(31,31,39,0.08)] overflow-hidden',
				className,
			)}
		>
			<button
				type="button"
				onClick={() => onSelect(undefined)}
				className={cn(
					'flex items-center justify-between gap-2 m-2 mb-0 h-9 px-3 rounded-lg border border-solid text-[13px] font-semibold whitespace-nowrap transition cursor-pointer font-[inherit]',
					selectedTruckId === undefined
						? 'bg-track-indigo border-track-indigo text-white'
						: 'bg-white border-track-line text-track-ink hover:bg-track-hover',
				)}
			>
				<T keyName="orderTracking.showAllTrucks">Show all trucks</T>
				<span
					className={cn(
						'text-[12px] font-medium',
						selectedTruckId === undefined
							? 'text-white/80'
							: 'text-track-muted',
					)}
				>
					{trucks.length}
				</span>
			</button>
			<div className="flex flex-col gap-1 p-2 max-h-[200px] sm:max-h-[260px] overflow-y-auto">
				{trucks.map((truck) => {
					const assigned = isTruckAssigned(truck);
					const selected = truck.id === selectedTruckId;

					return (
						<button
							key={truck.id}
							type="button"
							onClick={() => onSelect(truck.id)}
							disabled={!assigned}
							title={
								assigned
									? truck.plate || undefined
									: t(
											'orderTracking.routeAvailableAfterAssignment',
											'Route available once a truck is assigned',
									  )
							}
							className={cn(
								'flex-none flex items-center justify-between gap-2 h-9 px-3 rounded-lg border border-solid text-[13px] font-semibold whitespace-nowrap transition text-left font-[inherit]',
								!assigned
									? 'bg-track-orange border-track-orange text-track-ink cursor-not-allowed'
									: selected
									? 'cursor-pointer bg-track-indigo border-track-indigo text-white'
									: 'cursor-pointer bg-white border-track-line text-track-ink hover:bg-track-hover',
							)}
						>
							<T
								keyName="orderTracking.truckNumber"
								params={{ number: truck.number }}
							>
								{'Truck #{number}'}
							</T>
							{!assigned ? (
								<span className="text-[12px] font-medium">
									<T keyName="orderTracking.notAssigned">Not assigned</T>
								</span>
							) : truck.plate ? (
								<span
									className={cn(
										'text-[12px] font-medium truncate',
										selected ? 'text-white/80' : 'text-track-muted',
									)}
								>
									{truck.plate}
								</span>
							) : null}
						</button>
					);
				})}
			</div>
		</div>
	);
};

export default TruckSwitcher;
